import { apiClient } from '../utils/apiClient';
import { getSAProxyInvestorId, saProxyApiClient } from './saProxyAdapter';

class PdfService {
    private baseUrl = '/api/investor/pdf';

    // SA proxy mode (2026-09-06): raw apiClient sent the AGENT's token to investor pdf
    // endpoints. Same client pick as profile/investor services; adaptPath rewrites only
    // the endpoints that have SA counterparts, everything else is passed through unchanged.
    private get client() {
        return getSAProxyInvestorId() !== null ? saProxyApiClient : apiClient;
    }

    /**
     * Download investor information PDF for current user (Summary version)
     * Aligned with Laravel: user-print-preview-final.blade.php
     */
    async downloadInvestorPdf(): Promise<Blob> {
        const response = await this.client.get<Blob>(`${this.baseUrl}/view`, {
            responseType: 'blob'
        });
        return response.data;
    }

    /**
     * Get investor print preview HTML for current user (Summary version)
     * Aligned with Laravel: user-print-preview-final.blade.php
     */
    async getPrintPreviewHtml(): Promise<string> {
        const response = await this.client.get<string>(`${this.baseUrl}/print-preview`, {
            responseType: 'text'
        });
        return response.data;
    }

    /**
     * Helper to trigger PDF download in browser (Summary version)
     */
    async downloadPdfFile(filename?: string): Promise<void> {
        const blob = await this.downloadInvestorPdf();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || 'investor_information.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    /**
     * Helper to open print preview in new window (Summary version)
     */
    async openPrintPreview(): Promise<void> {
        const html = await this.getPrintPreviewHtml();
        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.write(html);
            printWindow.document.close();
        }
    }

    // ─── KYC Form (36-page Account Opening Kit) ───

    /** Download the filled KYC form PDF for the current user (journey-scoped when given). */
    async downloadKycFormPdf(journeyId?: string): Promise<Blob> {
        const response = await this.client.get<Blob>(`${this.baseUrl}/kyc-form`, {
            params: journeyId ? { journeyId } : undefined,
            responseType: 'blob'
        });
        return response.data;
    }

    /** "Download all": every document the investor has to sign for the journey, as one zip. */
    async downloadOnboardingPack(journeyId?: string): Promise<Blob> {
        const response = await this.client.get<Blob>(`${this.baseUrl}/onboarding-pack`, {
            params: journeyId ? { journeyId } : undefined,
            responseType: 'blob'
        });
        return response.data;
    }

    // ─── Standalone regulator forms, same pattern as the booklet above ───

    /** Download the filled CERSAI CKYC Individual form. */
    async downloadCkycFormPdf(journeyId?: string): Promise<Blob> {
        const response = await this.client.get<Blob>(`${this.baseUrl}/ckyc-form`, {
            params: journeyId ? { journeyId } : undefined,
            responseType: 'blob'
        });
        return response.data;
    }

    /** Download the filled FATCA / CRS declaration. */
    async downloadFatcaFormPdf(journeyId?: string): Promise<Blob> {
        const response = await this.client.get<Blob>(`${this.baseUrl}/fatca-form`, {
            params: journeyId ? { journeyId } : undefined,
            responseType: 'blob'
        });
        return response.data;
    }


    /** Get the structured form data JSON (for the edit UI). */
    async getKycFormData(): Promise<any> {
        const response = await this.client.get(`${this.baseUrl}/kyc-form/data`);
        return response.data;
    }

    /** Trigger browser download of the KYC form PDF. */
    async downloadKycFormFile(filename?: string): Promise<void> {
        const blob = await this.downloadKycFormPdf();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || 'KYC_Account_Opening_Kit.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

}

export const pdfService = new PdfService();
